import React, { useEffect, useMemo, useState } from 'react';
import { Card, Empty, Select, Table } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import PartnerSites from './PartnerSites';
import PartnerSchedules from './PartnerSchedules';
import PartnerCollectionPoints from './PartnerCollectionPoints';
import { getPartners, getPartnerCollectedItems } from '../modules/partners/api';
import BreadCrumb from '../modules/breadcrumbs';
import { tenantConfig } from '../config/tenantConfig';

/**
 * OVO-133 — global, top-level Sites / Recurring / Collected pages.
 *
 * Sites, Recurring schedules and Collected items are partner-scoped, so
 * the global page is a thin wrapper: a partner picker (URL-synced via
 * `?partnerId=`) on top, and the existing partner-scoped component below.
 * This lifts the three out of "hidden inside Partner detail" into the
 * top nav while reusing all the existing functionality.
 *
 * @param {'sites'|'recurring'|'collected'} kind
 */
export default function AdminCatalogPage({ kind }) {
  const { t } = useTranslation(['menu', 'partners']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [partners, setPartners] = useState([]);

  const partnerId = searchParams.get('partnerId') ? Number(searchParams.get('partnerId')) : undefined;

  useEffect(() => {
    let cancelled = false;
    getPartners({ take: 500, page: 1 }).then(({ response }) => {
      if (cancelled) return;
      setPartners(response?.partners || response?.items || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // The picker holds the partner list (id + name + code), so we can pass the
  // full object to PartnerSites/PartnerSchedules without an extra fetch.
  // OVO-132 — a single-tenant instance has exactly one partner; auto-bind it and
  // hide the picker (the URL `?partnerId=` scope is moot with one partner).
  const partner = useMemo(() => {
    if (tenantConfig.isSingleTenant) return partners[0] || null;
    return partners.find((p) => p.id === partnerId) || null;
  }, [partners, partnerId]);

  const onPick = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('partnerId', String(id));
    else next.delete('partnerId');
    setSearchParams(next);
  };

  const title = t(kind);

  return (
    <div className="Content AdminCatalogPage">
      <div className="ContentHeader">
        <BreadCrumb breadCrumbs={[{ text: String(title) }]} />
      </div>

      {/* Partner scope picker — hidden in a single-tenant instance */}
      {!tenantConfig.isSingleTenant && (
        <div
          style={{
            background: 'var(--surface-1)',
            padding: '12px 24px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {t('partners:title', 'Partner')}
          </span>
          <Select
            showSearch
            allowClear
            placeholder={t('selectPartner')}
            style={{ minWidth: 320 }}
            value={partnerId}
            onChange={onPick}
            optionFilterProp="label"
            options={partners.map((p) => ({ value: p.id, label: p.name }))}
          />
        </div>
      )}

      {!partner ? (
        <div style={{ padding: 48 }}>
          <Empty description={t('pickPartnerToView', { what: title })} />
        </div>
      ) : kind === 'sites' ? (
        <div style={{ padding: 16 }}>
          <PartnerSites partner={partner} />
        </div>
      ) : kind === 'recurring' ? (
        <div style={{ padding: 16 }}>
          <PartnerSchedules partner={partner} />
        </div>
      ) : kind === 'collection-points' ? (
        <PartnerCollectionPoints partner={partner} />
      ) : (
        <CollectedTable partnerId={partner.id} />
      )}
    </div>
  );
}

// Collected-empties table (the PartnerDetail "Collected Empties" tab is
// inline, so the global page renders its own thin table from the same API).
function CollectedTable({ partnerId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});

  const load = (page = 1) => {
    setLoading(true);
    getPartnerCollectedItems(partnerId, { page }).then(({ response }) => {
      setItems(response?.items || []);
      setPagination(response?.pagination || {});
      setLoading(false);
    });
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  return (
    <div style={{ padding: 16 }}>
      <Card>
        <Table
          dataSource={items}
          rowKey="id"
          loading={loading}
          size="small"
          locale={{ emptyText: 'No collected items' }}
          pagination={{
            current: pagination.currentPage || 1,
            pageSize: 20,
            total: pagination.totalItems || 0,
            showTotal: (total) => `${total} items`,
          }}
          onChange={(pag) => load(pag.current)}
          columns={[
            {
              title: 'Date',
              dataIndex: 'collectedAt',
              key: 'collectedAt',
              width: 150,
              render: (val) => (val ? moment(val).format('DD/MM/YYYY HH:mm') : '-'),
            },
            {
              title: 'Route',
              dataIndex: ['route', 'id'],
              key: 'routeId',
              width: 90,
              render: (val) => (val ? <Link to={`/admin/routes/${val}`}>{val}</Link> : '-'),
            },
            {
              title: 'Order Ref',
              key: 'orderRef',
              width: 150,
              render: (_, r) => r.order?.partnerOrderRef || r.order?.orderCode || '-',
            },
            {
              title: 'Item Type',
              key: 'itemType',
              render: (_, r) => r.partnerItemType?.name || r.description || '-',
            },
            {
              title: 'Qty',
              dataIndex: 'quantity',
              key: 'quantity',
              width: 80,
              render: (v) => v ?? 1,
            },
          ]}
        />
      </Card>
    </div>
  );
}
